import { Flex, Notice, SidebarContent, SidebarPart } from '@eleveo/components-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { WebexAuthButton } from './WebexAuthButton';
import { useIntegrations } from '../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../constants/dataTestIds';
import { ONE_SECOND } from '../../../constants/timeConstants';
import { getSupportedJobs, getSupportedStreams } from '../../../scene/integrations/config';
import {
  INTEGRATION_FORM_FIELDS,
  TEMPORARY_FORM_FIELDS,
  WebexCallingConfigurationType,
} from '../../../scene/integrations/types';
import { getWebexCallingRootState } from '../../../utils/formUtils';
import { ETL_INTEGRATION_TYPE } from '../../configuration/types';
import { IntegrationTitle } from '../../sideBar/integrations/IntegrationTitle';
import { IntegrationsHeader } from '../../sideBar/integrations/IntegrationsHeader';
import { FieldApiUrl } from '../fields/FieldApiUrl';
import { FieldClientId } from '../fields/FieldClientId';
import { FieldClientSecret } from '../fields/FieldClientSecret';
import { FieldIntegrationName } from '../fields/FieldIntegrationName';
import { FieldOrganizationId } from '../fields/FieldOrganizationId';
import { IntegrationJobs } from '../jobs/IntegrationJobs';
import { TeamSelector } from '../jobs/userSync/TeamSelector';
import { TeamSelectorProvider } from '../teamselector/TeamSelectorProvider';

export const WebexCallingForm: React.FC = () => {
  const { t } = useTranslation();

  const { integrationType } = useIntegrations();

  if (integrationType !== ETL_INTEGRATION_TYPE.wxcalling) throw new Error('WebexCallingForm: invalid integrationType!');

  const [isWarningActive, setIsWarningActive] = useState(false);

  const { batch, change } = useForm();
  const { values: formState } = useFormState<WebexCallingConfigurationType>({ subscription: { values: true } });

  const { wxcallingExpiresIn, wxcallingClientId, wxcallingClientSecret, wxcallingRefreshTokenExpiresIn, startTime } =
    getWebexCallingRootState(formState);

  const normalizeData = useCallback(() => {
    /**
     * If there is `startTime` defined in form, Webex login was performed.
     * Subtract delta time from expiration times according to BE requirements.
     * Also remove `startTime`.
     */
    if (startTime !== undefined && wxcallingRefreshTokenExpiresIn && wxcallingExpiresIn) {
      const delta = Math.ceil((Date.now() - startTime) / ONE_SECOND);
      batch(() => {
        change(
          INTEGRATION_FORM_FIELDS.refreshTokenExpires(ETL_INTEGRATION_TYPE.wxcalling),
          wxcallingRefreshTokenExpiresIn - delta
        );
        change(INTEGRATION_FORM_FIELDS.accessTokenExpires(ETL_INTEGRATION_TYPE.wxcalling), wxcallingExpiresIn - delta);
        change(TEMPORARY_FORM_FIELDS.startTime, undefined);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, wxcallingRefreshTokenExpiresIn, wxcallingExpiresIn]);

  useEffect(() => {
    if (wxcallingExpiresIn === undefined) {
      // no authorization, don't show warning
      return;
    }
    setIsWarningActive(true);
  }, [wxcallingClientId, wxcallingClientSecret, wxcallingExpiresIn]);

  const availableJobs = useMemo(() => getSupportedJobs(integrationType), [integrationType]);
  const availableStreams = useMemo(() => getSupportedStreams(integrationType), [integrationType]);

  return (
    <TeamSelectorProvider>
      <IntegrationsHeader beforeSubmit={normalizeData} />
      <SidebarContent>
        <SidebarPart>
          <Flex column gap={20} className="p-20">
            <IntegrationTitle />
            <FieldIntegrationName />
            <FieldApiUrl />
            <FieldOrganizationId />
            <FieldClientId />
            <FieldClientSecret />

            {isWarningActive && (
              <Notice color="warning" testId={TEST_IDS.noticeConfigurationWarning}>
                {t('myConfigurations.warning')}
              </Notice>
            )}
            <IntegrationJobs availableJobs={availableJobs} availableStreams={availableStreams} />

            <WebexAuthButton className="mbs-20 w-min" />
          </Flex>
        </SidebarPart>
        <SidebarPart width={600}>
          <TeamSelector />
        </SidebarPart>
      </SidebarContent>
    </TeamSelectorProvider>
  );
};
